/*
 * Purpose: Manage AuthForm state and actions for login, registration, and password reset flows.
 * Boundary: Custom hook only; owns local state, async logic, and derived values; no UI.
 * Invariants:
 * - Defaults to a valid mode ('login' | 'register' | 'forgot-password').
 * - Uses PhoneNumber.getNumberDetails for cell number validation.
 * - ASCII only; uses "..." for status strings instead of Unicode ellipses.
 */

// Imports
import { useState, useTransition, useEffect, useMemo, useCallback, useContext } from 'react';
import { useIDValidation } from './authform-idnumber-hooks';
import { AgeContext } from '../authform';
import { useToast } from '../../toast/toast-slim';

// Fallback constants (used if server fetch fails)
const TCS_MINOR_FALLBACK = 'I acknowledge that I am between 16 and 18 years old, a South African citizen, and that my parent or legal guardian is registering me on Oaths.co.za and has accepted the digital Pledge on my behalf. My personal information and documents will be submitted for secure digital certification, processed in full compliance with POPIA, and subject to legal audit, fraud prevention, and identity verification by Oaths.co.za.';
const TCS_ADULT_FALLBACK = 'I confirm and accept this digital Pledge: I am a South African citizen, over 18, acting on my own behalf, and I willingly submit my personal information and documents for secure digital certification. I understand this information will be processed in full compliance with POPIA, and that all certifications are subject to legal audit, fraud prevention, and identity verification by Oaths.co.za.';
const TRACKING_ACKNOWLEDGMENT_MINOR = 'I acknowledge that my parent/legal guardian may consent to tracking on my behalf.';

// Hook useAuthForm:
// Encapsulates all form state, validation, and async submission logic
export function useAuthForm({ mode = 'login', auth, guardianState, referralCode } = {}) {
    // Toasts
    const toastr = useToast();

    // Grouped state: Form data and UI
    const [formState, setFormState] = useState({ idNumber: '', firstName: '', lastName: '' });
    const [uiState, setUiState] = useState({ status: '', mode });

    // Core flags - FIXED: use uiState.mode, not prop mode
    const isRegister = uiState.mode === 'register';
    const isForgotPassword = uiState.mode === 'forgot-password';

    // Sub-component states
    const [passwordState, setPasswordState] = useState({ isValid: false, password: '' });
    const [tcsState, setTcsState] = useState({ checked: [], isValid: false });
    const [otpState, setOtpState] = useState({ isValid: false, otpValue: '', hasRequested: false, cooldown: 0 });
    const [otpResetSignal, setOtpResetSignal] = useState(0);
    const [cellNumber, setCellNumber] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [countryCode, setCountryCode] = useState('ZA');

    // Compound password+OTP state for forgot-password flow
    const [passwordWithOtpState, setPasswordWithOtpState] = useState({
        isPasswordValid: false,
        isOtpValid: false,
        isValid: false,
        password: '',
        otpValue: ''
    });

    // Derived: ID validation and age
    const { isValidID, age } = useIDValidation(formState.idNumber);

    // Derived: Minor flag
    const isMinor = age !== null && age < 18;

    // Derived: International phone gate (registration only)
    const isInternationalPhone = useMemo(
        () => !!cellNumber && !cellNumber.startsWith('27'),
        [cellNumber]
    );
    const internationalGateMessage = useMemo(
        () => isRegister && isInternationalPhone && !isValidID
            ? 'International numbers are currently available for South African citizens only. Please enter a valid SA ID number.'
            : null,
        [isRegister, isInternationalPhone, isValidID]
    );

    // Tracking consent state: null = not yet chosen, true = accepted, false = declined
    const [trackingConsentChoice, setTrackingConsentChoice] = useState(null);

    // Minor acknowledgment checkboxes (individual inline checkboxes, not the Accept button)
    const [minorTcAck, setMinorTcAck] = useState(false);
    const [minorTrackingAck, setMinorTrackingAck] = useState(false);

    // Server-provided T&C text (fetched once on mount)
    const [agreementText, setAgreementText] = useState({
        tcsAdult: TCS_ADULT_FALLBACK,
        tcsMinor: TCS_MINOR_FALLBACK,
        trackingConsentAdult: '',
        trackingConsentMinor: '',
        trackingAcknowledgmentMinor: TRACKING_ACKNOWLEDGMENT_MINOR
    });
    useEffect(() => {
        if (!auth?.getRegistrationAgreementText) return;
        auth.getRegistrationAgreementText()
            .then(res => {
                const d = res?.data;
                if (d?.tcsAdult && d?.tcsMinor) {
                    setAgreementText({
                        tcsAdult: d.tcsAdult,
                        tcsMinor: d.tcsMinor,
                        trackingConsentAdult: d.trackingConsentAdult || '',
                        trackingConsentMinor: d.trackingConsentMinor || '',
                        trackingAcknowledgmentMinor: d.trackingAcknowledgmentMinor || TRACKING_ACKNOWLEDGMENT_MINOR
                    });
                }
            })
            .catch(() => { /* keep fallback */ });
    }, [auth]);

    // Derived: TCs to accept (adult only -- minor uses inline acknowledgment checkboxes)
    const tcsToAccept = useMemo(
        () => (isRegister && !isMinor ? [agreementText.tcsAdult] : []),
        [isRegister, isMinor, agreementText]
    );

    // Derived: tracking consent text (adult only -- minor acknowledges via checkbox)
    const trackingConsentText = useMemo(
        () => isMinor ? '' : agreementText.trackingConsentAdult,
        [isMinor, agreementText]
    );

    // Derived: minor acknowledgment items for inline checkboxes
    const minorAcknowledgments = useMemo(() => {
        if (!isRegister || !isMinor) return [];
        return [
            { text: agreementText.tcsMinor, checked: minorTcAck, onChange: setMinorTcAck },
            { text: agreementText.trackingAcknowledgmentMinor, checked: minorTrackingAck, onChange: setMinorTrackingAck },
        ];
    }, [isRegister, isMinor, agreementText, minorTcAck, minorTrackingAck]);

    // Effect: sync minor acknowledgment state to tcsState and trackingConsentChoice
    useEffect(() => {
        if (isMinor && isRegister) {
            const allAcked = minorTcAck && minorTrackingAck;
            setTcsState({ checked: allAcked, isValid: allAcked });
            setTrackingConsentChoice(minorTrackingAck ? true : null);
        }
    }, [isMinor, isRegister, minorTcAck, minorTrackingAck]);

    // Handlers: Form field updates
    const setField = useCallback((key, value) => {
        setFormState(prev => ({ ...prev, [key]: value }));
    }, []);

    // Handlers: Mode update
    const setMode = useCallback((newMode) => {
        setUiState(prev => ({ ...prev, mode: newMode }));
    }, []);

    // Derived: Required fields for OTP enabling
    // Forgot-password: also requires password valid before OTP buttons unlock
    const isProceedReadyOtp = useMemo(
        () => {
            const baseReady = !!formState.idNumber && isValidID && !!cellNumber && isPhoneValid;
            if (isForgotPassword) return baseReady && passwordWithOtpState.isPasswordValid;
            return baseReady;
        },
        [formState.idNumber, isValidID, cellNumber, isPhoneValid, isForgotPassword, passwordWithOtpState.isPasswordValid]
    );

    // Derived: Required fields for submission
    const proceedRequired = useMemo(() => {
        if (isForgotPassword) {
            return {
                id: !!formState.idNumber && isValidID,
                cell: !!cellNumber && isPhoneValid,
                passwordWithOtp: passwordWithOtpState.isValid,
            };
        }
        
        return {
            id: !!formState.idNumber && isValidID,
            first: !isRegister || !!formState.firstName,
            last: !isRegister || !!formState.lastName,
            cell: !!cellNumber && isPhoneValid,
            tcs: !isRegister || (isMinor
                ? (minorTcAck && minorTrackingAck)
                : ((tcsState?.isValid ?? false) && trackingConsentChoice !== null)),
            password: !!passwordState?.password && (passwordState?.isValid ?? false),
            guardian: !isRegister || !isMinor || (
                (guardianState?.isValid ?? false) &&
                guardianState?.idNumber !== formState.idNumber &&
                guardianState?.cellNumber !== cellNumber
            ),
        };
    }, [formState, isValidID, isRegister, isForgotPassword, cellNumber, isPhoneValid, tcsState, trackingConsentChoice, passwordState, passwordWithOtpState, isMinor, guardianState, minorTcAck, minorTrackingAck]);

    // Derived: Submission readiness
    const isProceedReady = useMemo(() => Object.values(proceedRequired).every(Boolean), [proceedRequired]);

    // Derived: Register OTP gate -- all fields except OTP must be valid before requesting
    const isProceedReadyOtpRegister = useMemo(
        () => isRegister ? isProceedReady : false,
        [isRegister, isProceedReady]
    );

    const isLoginReady = useMemo(
        () => proceedRequired.id && proceedRequired.cell && proceedRequired.password,
        [proceedRequired]
    );

    const isRegisterReady = useMemo(
        () => isProceedReady && (otpState?.isValid ?? false),
        [isProceedReady, otpState?.isValid]
    );

    const isResetPasswordReady = useMemo(
        () => isProceedReady,
        [isProceedReady]
    );

    // Derived: Missing fields list
    const missingList = useCallback(() => {
        const fields = [];
        if (!proceedRequired.id) fields.push('ID');
        if (proceedRequired.first !== undefined && !proceedRequired.first) fields.push('First name');
        if (proceedRequired.last !== undefined && !proceedRequired.last) fields.push('Last name');
        if (!proceedRequired.cell) fields.push('Cell number');
        if (proceedRequired.tcs !== undefined && !proceedRequired.tcs) fields.push('Terms and Conditions');
        if (proceedRequired.password !== undefined && !proceedRequired.password) fields.push('Password');
        if (proceedRequired.passwordWithOtp !== undefined && !proceedRequired.passwordWithOtp) fields.push('Password + OTP');
        if (proceedRequired.guardian !== undefined && !proceedRequired.guardian) fields.push('Legal Guardian');
        return fields;
    }, [proceedRequired]);

    // Async submission state
    const [isPending, startTransition] = useTransition();

    // Handlers: Login request
    const requestLogin = useCallback((e) => {
        if (e) e.preventDefault();

        if (!isLoginReady) {
            toastr.error('Please complete required fields.');
            return;
        }

        startTransition(async () => {
            try {
                const reqDto = {
                    idNumber: formState.idNumber,
                    cellphoneNumber: cellNumber,
                    countryCode: countryCode,
                    password: passwordState.password,
                };

                var results = await auth.requestLogin(reqDto);
                if (results.isSuccess && results.data) {
                    window.location.href = results.data;
                }
                else {
                    setShowTransition(false);
                    toastr.error(results?.message || 'Login failed.', 0);
                }
            } catch (err) {
                setShowTransition(false);
                toastr.error(err.data?.message || 'Login failed.', 0);
            }
        });
    }, [auth, formState.idNumber, cellNumber, passwordState.password, startTransition, isLoginReady, toastr]);

    // Handlers: Registration request
    const requestRegistration = useCallback((e) => {
        if (e) e.preventDefault();

        if (!isRegisterReady) {
            toastr.error('Please complete required fields.');
            return;
        }

        startTransition(async () => {
            try {
                const reqDto = {
                    idNumber: formState.idNumber,
                    firstName: formState.firstName,
                    lastName: formState.lastName,
                    cellphoneNumber: cellNumber,
                    countryCode: countryCode,
                    password: passwordState.password,
                    otpValue: otpState.otpValue,
                    legalGuardian: guardianState ? {
                        firstName: guardianState.firstName,
                        lastName: guardianState.lastName,
                        idNumber: guardianState.idNumber,
                        cellphoneNumber: guardianState.cellNumber,
                    } : null,
                    acceptedTcs: tcsState?.checked ?? [],
                    trackingConsentAccepted: isMinor
                        ? (guardianState?.guardianTrackingConsentChoice ?? null)
                        : trackingConsentChoice,
                    referralCode: referralCode,
                };

                var results = await auth.requestRegistration(reqDto);
                if (results.isSuccess && results.data) {
                    window.location.href = results.data;
                }
                else {
                    setShowTransition(false);
                    setOtpResetSignal(s => s + 1);
                    toastr.error(results?.message || 'Registration failed. Please check your details and try again.', 0);
                }
            } catch (err) {
                setShowTransition(false);
                setOtpResetSignal(s => s + 1);
                toastr.error(err.data?.message || 'Registration failed. Please check your details and try again.', 0);
            }
        });
    }, [auth, formState.idNumber, formState.firstName, formState.lastName, cellNumber, countryCode, passwordState.password, otpState.otpValue, guardianState?.value, tcsState?.checked, startTransition, isRegisterReady, toastr, referralCode]);

    // Handlers: Password reset request
    const requestPasswordReset = useCallback((e) => {
        if (e) e.preventDefault();

        if (!isResetPasswordReady) {
            toastr.error('Please complete required fields.');
            return;
        }

        startTransition(async () => {
            try {
                const reqDto = {
                    idNumber: formState.idNumber,
                    cellphoneNumber: cellNumber,
                    countryCode: countryCode,
                    password: passwordWithOtpState.password,
                    otpValue: passwordWithOtpState.otpValue,
                };

                var results = await auth.resetPassword(reqDto);
                if (results.isSuccess) {
                    toastr.success('Password updated. Returning to login.');
                    setMode('login');
                }
                else {
                    toastr.error(results?.message || 'Password update failed.');
                }
            } catch (err) {
                toastr.error('Password reset failed.');
            }
        });
    }, [auth, formState.idNumber, cellNumber, passwordWithOtpState.password, passwordWithOtpState.otpValue, startTransition, isResetPasswordReady, toastr, setMode]);

    // Effects: Push age to parent via context
    const { setAge } = useContext(AgeContext) || {};
    useEffect(() => {
        if (setAge) setAge(age);
    }, [age, setAge]);

    // Transition overlay state -- consistent with legal guardian pattern
    const [showTransition, setShowTransition] = useState(false);
    const [transitionMessage, setTransitionMessage] = useState('');

    // Effect: When OTP validates in register mode, show transition then fire register
    // By the time this fires, otpState.otpValue is set and isRegisterReady is true
    useEffect(() => {
        if (otpState.isValid && isProceedReady && isRegister) {
            // 1. Show transition (fire and forget)
            setTransitionMessage('Registering your account...');
            setShowTransition(true);
            // 2. Fire register (fire and forget)
            requestRegistration();
        }
    }, [otpState.isValid]);

    // Handler: Login with transition overlay
    const requestLoginWithTransition = useCallback((e) => {
        if (e) e.preventDefault();
        if (!isLoginReady) {
            toastr.error('Please complete required fields.');
            return;
        }
        // 1. Show transition (fire and forget)
        setTransitionMessage('Logging in...');
        setShowTransition(true);
        // 2. Fire login (fire and forget)
        requestLogin();
    }, [isLoginReady, requestLogin, toastr]);

    // Return Shape - expose grouped state and handlers
    return {
        isRegister,
        isForgotPassword,
        mode: uiState.mode,
        setMode,
        form: formState,
        setField,
        tcsToAccept,
        acceptedTCs: tcsState,
        setAcceptedTCs: setTcsState,
        trackingConsentText,
        trackingConsentChoice,
        setTrackingConsentChoice,
        minorAcknowledgments,
        status: uiState.status,
        isPending,
        proceedRequired,
        isProceedReady,
        isProceedReadyOtp,
        isProceedReadyOtpRegister,
        missingList,
        requestLogin: requestLoginWithTransition,
        requestRegistration,
        requestPasswordReset,
        auth,
        setPasswordState,
        setPasswordWithOtpState,
        otpState,
        setOtpState,
        age,
        isMinor,
        cellNumber,
        setCellNumber,
        isPhoneValid,
        setIsPhoneValid,
        countryCode,
        setCountryCode,
        guardianState,
        isLoginReady,
        isRegisterReady,
        isResetPasswordReady,
        isPasswordValid: passwordWithOtpState.isPasswordValid,
        internationalGateMessage,
        showTransition,
        transitionMessage,
        setShowTransition,
        otpResetSignal,
    };
}